import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';
import type { ExpertReviewRejectReasonObject as ExpertReviewRejectReasonBase } from './ExpertReviewModule';

export interface ExpertReviewRejectReasonObject
  extends ExpertReviewRejectReasonBase {
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExpertReviewRejectReasonsListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  items: Array<ExpertReviewRejectReasonObject>;
  errors: unknown;
}

@Module
export default class ExpertReviewRejectReasonsModule
  extends VuexModule
  implements ExpertReviewRejectReasonsListInfo
{
  items = [] as Array<ExpertReviewRejectReasonObject>;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  get expertReviewRejectReasonsList(): Array<ExpertReviewRejectReasonObject> {
    return this.items;
  }

  get expertReviewRejectReasonsListCount(): number {
    return this.totalItems;
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_REJECT_REASONS_LIST](data) {
    this.items = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_REJECT_REASON_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.RESET_EXPERT_REVIEW_REJECT_REASONS_LIST]() {
    this.items = [] as Array<ExpertReviewRejectReasonObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Action
  [Actions.CREATE_EXPERT_REVIEW_REJECT_REASON](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/indices/expert-review/reject-reasons', data)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
        this.context.commit(Mutations.RESET_EXPERT_REVIEW_REJECT_REASONS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_EXPERT_REVIEW_REJECT_REASON]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/indices/expert-review/reject-reasons', id, data)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
        this.context.commit(Mutations.RESET_EXPERT_REVIEW_REJECT_REASONS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_EXPERT_REVIEW_REJECT_REASON](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/indices/expert-review/reject-reasons', id)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
        this.context.commit(Mutations.RESET_EXPERT_REVIEW_REJECT_REASONS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_EXPERT_REVIEW_REJECT_REASON_CURRENT_PAGE](val) {
    this.context.commit(
      Mutations.SET_EXPERT_REVIEW_REJECT_REASON_CURRENT_PAGE,
      val
    );
  }

  @Action
  [Actions.GET_EXPERT_REVIEW_REJECT_REASON](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/indices/expert-review/reject-reasons', id);
  }

  @Action
  [Actions.GET_EXPERT_REVIEW_REJECT_REASONS_LIST]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/indices/expert-review/reject-reasons/list', {
      params: { page: this.currentPage },
    })
      .then(({ data }) => {
        this.context.commit(
          Mutations.SET_EXPERT_REVIEW_REJECT_REASONS_LIST,
          data
        );
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }
}
